/**
 * Created by pajicv on 10/18/18.
 */
import { Base64 } from 'js-base64';
import _ from 'lodash';
import { db } from '../firebase';
import { getNumberOfActiveTasksInProject } from '../../firebase/db';
import { MANAGER_ROLE, CO_MANAGER_ROLE, WORKER_ROLE } from '../../utils/constants';

export const getManagedProjects = email => db.ref(`users/${Base64.encode(email)}`)
  .orderByChild(`projects/${MANAGER_ROLE}`)
  .once('value')
  .then((snapshot) => {
    const projects = snapshot.val() || {};
    return Promise.all(Object.keys(projects.projects.manager).map(projectId => db.ref(`projects/${projectId}`)
      .once('value')
      .then((snapshot) => {
        const project = snapshot.key || {};
        return project;
      })));
  });

export const getUsersProjects = email => Promise.all([
  db.ref(`users/${Base64.encode(email)}/projects/${MANAGER_ROLE}`)
    .once('value')
    .then(snapshot => snapshot.val() || {}),
  db.ref(`users/${Base64.encode(email)}/projects/${WORKER_ROLE}`)
    .once('value')
    .then(snapshot => snapshot.val() || {}),
  db.ref(`users/${Base64.encode(email)}/projects/${CO_MANAGER_ROLE}`)
    .once('value')
    .then(snapshot => snapshot.val() || {})
])
  .then((projects) => {
    const allProjects = Object.keys(Object.assign({}, projects[0], projects[1], projects[2]));
    return allProjects;
  });

export const getProjectTitle = projectId => db.ref(`projects/${projectId}`)
  .once('value')
  .then((snapshot) => {
    const project = snapshot.val() || {};
    return { [snapshot.key]: project.title };
  });

export const getUsersProjectTitles = email => getUsersProjects(email)
  .then(projects => Promise.all(projects.map(getProjectTitle)));

export const getAllInvolvedUsers = email => getUsersProjects(email)
  .then(projects => Promise.all(projects.map(projectId => db.ref(`projects/${projectId}/members`).once('value').then(snapshot => snapshot.val() || {}))))
  .then(allMembers => allMembers.reduce((acc, member) => Object.assign(acc, member), {}))
  .then(allMembers => Object.keys(allMembers).map(member => ({ label: Base64.decode(member), value: member })));

export const getAllRelatedDocuments = email => getUsersProjects(email)
  .then(projects => Promise.all(projects.map(projectId => db.ref('documents')
    .orderByChild('project')
    .equalTo(projectId)
    .once('value')
    .then(snapshot => snapshot.val() || {}))))
  .then(documents => documents.reduce((acc, doc) => Object.assign(acc, doc), {}));

export const getFieldValue = (collection, id, field) => db.ref(`${collection}/${id}`)
  .once('value')
  .then(snapshot => {
    if (collection === 'cycles') {
      const cycle = snapshot.val();
      if (!cycle) return;
      return {
        label: cycle[field] ? `Cy ${cycle.index + 1} : ${cycle[field]}` : `Cy ${cycle.index + 1}`,
        value: id
      }
    }
    return {
      label: snapshot.val()[field],
      value: id
    }
  });

export const getProjectTitles = projects => Promise.all(projects.map(projectId => getFieldValue('projects', projectId, 'title')));

export const getCycleTitles = cycles => Promise.all(cycles.map(cycleId => getFieldValue('cycles', cycleId, 'title')));

export const getTaskTitles = tasks => Promise.all(tasks.map(taskId => getFieldValue('tasks', taskId, 'name')));

export const getAuthorNames = authors => Promise.all(authors.map(userId => ({ label: Base64.decode(userId), value: userId })));

export const getFileTypes = fileTypes => fileTypes.map(fileType => ({ label: fileType, value: fileType }));

export const prepareFilters = (documents) => {
  const documentsArray = Object.keys(documents).map(documentId => documents[documentId]);
  const filters = documentsArray.reduce((result, document) => {
    result.projects.push(document.project);
    if (document.cycle) {
      result.cycles.push(document.cycle);
    }
    if (document.task) {
      result.tasks.push(document.task);
    }
    result.authors.push(document.user);
    result.fileTypes.push(document.name.split('.').pop());
    return result;
  }, {
      projects: [],
      cycles: [],
      tasks: [],
      authors: [],
      fileTypes: []
    });

  return Promise.all([
    getProjectTitles(_.uniq(filters.projects)),
    getCycleTitles(_.uniq(filters.cycles)),
    getTaskTitles(_.uniq(filters.tasks)),
    getAuthorNames(_.uniq(filters.authors)),
    getFileTypes(_.uniq(filters.fileTypes))
  ]);
};

export const isHavingOpenTasks = (email) => db.ref(`users/${Base64.encode(email)}/projects/${WORKER_ROLE}`)
  .once('value')
  .then(snapshot => snapshot.val() || {})
  .then(projects => {
    return Promise.all(Object.keys(projects).map(projectId => {
      return db.ref('subTasks')
        .orderByChild('project')
        .equalTo(projectId)
        .once('value')
        .then((subTasksSnapshot) => {
          const subTasks = subTasksSnapshot.val() || {};
          return Object.keys(subTasks).filter(subTaskId => (subTasks[subTaskId].assignedTo === Base64.encode(email)) && !subTasks[subTaskId].finished).length;
        })
    }))
      .then((assignedNotFinishedTasks) => assignedNotFinishedTasks.reduce((acc, numberOfTask) => numberOfTask ? acc + numberOfTask : acc, 0));
  });

export const getNumberOfMyOpenTasksOnProject = (projectId, email) => db.ref('subTasks')
  .orderByChild('project')
  .equalTo(projectId)
  .once('value')
  .then(snapshot => snapshot.val() || {})
  .then(subTasks => {
    const number = Object.keys(subTasks).filter(subTaskId => {
      return (subTasks[subTaskId].assignedTo === Base64.encode(email)) && !subTasks[subTaskId].finished;
    }).length;
    return number;
  });

export const getNumberOfMyOpenTasksOnProject_v2 = (projectId, email) => db.ref('tasks')
  .orderByChild('project')
  .equalTo(projectId)
  .once('value')
  .then(snapshot => snapshot.val() || {})
  .then(tasks => Promise.all(Object.keys(tasks).filter(taskId => tasks[taskId].active === true)
    .map(taskId => Promise.all(Object.keys(tasks[taskId].subTasks).map(subTaskId => db.ref(`subTasks/${subTaskId}`)
      .once('value')
      .then(snapshot => snapshot.val() || {})
      .then(subTask => {
        return (subTask.assignedTo === Base64.encode(email)) && !subTask.finished ? 1 : 0;
      }
      ))))))
  .then(numbers => numbers.reduce((sum, n) => sum + n.reduce((sum1, n1) => sum1 + n1, 0), 0));


export const getSubTasks = (tasks) => {
  const promiseArray = [];
  tasks && Object.keys(tasks).filter(taskId => (tasks[taskId] === true || tasks[taskId].active === true)).map((task, i) => {
    promiseArray.push(db.ref().child('subTasks').orderByChild('task').equalTo(task)
      .once('value')
      .then((snapshot) => {
        return snapshot.val();
      }));
  });
  return Promise.all(promiseArray)
    .then((arr) => {
      var obj = {};
      arr.map(item => {
        Object.assign(obj, item)
      });
      return obj;
    });
};

export const getProjectOwner = (projectId) => db.ref(`projects/${projectId}`)
  .once('value')
  .then(snapshot => snapshot.val() || {})
  .then(project => project.createdBy);



// WEBPACK FOOTER //
// ./src/firebase/db/project.js